// Freight route page configs — used by FreightRoutePage.
// Each config drives one SEO-focused landing page: /freight/<slug>.

export const GLOBAL_HUBS = [
  { city: "Singapore", role: "Global HQ", region: "Asia" },
  { city: "Hong Kong", role: "Regional HQ", region: "Asia" },
  { city: "London", role: "Regional HQ", region: "Europe" },
  { city: "Richmond", role: "Regional HQ", region: "North America" },
  { city: "São Paulo", role: "Regional HQ", region: "Latin America" },
];

export const COVERAGE_REGIONS = [
  {
    key: "africa",
    name: "Africa",
    lanes: ["Africa ↔ Asia", "Africa ↔ Europe", "Africa ↔ Americas"],
    countries: [
      "Nigeria", "Ghana", "Kenya", "Tanzania", "South Africa", "Zambia",
      "Ethiopia", "Ivory Coast", "Senegal", "Egypt", "Morocco", "Angola",
      "Cameroon", "Djibouti", "Togo", "Uganda", "Rwanda", "Mozambique",
    ],
  },
  {
    key: "asia",
    name: "Asia (East & West)",
    lanes: ["Asia ↔ Europe", "Asia ↔ North America", "Intra-Asia"],
    countries: [
      "China", "Singapore", "Japan", "South Korea", "Vietnam", "Thailand",
      "Malaysia", "Indonesia", "Philippines", "India", "Bangladesh",
      "Sri Lanka", "United Arab Emirates", "Saudi Arabia", "Pakistan", "Turkey",
    ],
  },
  {
    key: "europe",
    name: "Europe",
    lanes: ["Intra-Europe rail (30+ countries)", "Europe ↔ North America", "Europe ↔ Asia"],
    countries: [
      "Germany", "Netherlands", "United Kingdom", "France", "Italy", "Spain",
      "Poland", "Belgium", "Greece", "Portugal", "Romania", "Denmark",
      "Sweden", "Finland", "Czech Republic", "Hungary", "Austria", "Ireland",
    ],
  },
  {
    key: "north-america",
    name: "North America",
    lanes: ["North America ↔ Asia", "North America ↔ Europe", "North America ↔ Latin America"],
    countries: ["United States", "Canada", "Mexico"],
  },
  {
    key: "latin-america",
    name: "Latin America & Caribbean",
    lanes: ["Latin America ↔ Asia", "Latin America ↔ Europe", "Latin America ↔ North America"],
    countries: [
      "Brazil", "Argentina", "Chile", "Peru", "Colombia", "Ecuador",
      "Panama", "Dominican Republic", "Jamaica", "Trinidad & Tobago",
      "Uruguay", "Costa Rica", "Guatemala", "Venezuela",
    ],
  },
  {
    key: "oceania",
    name: "Oceania",
    lanes: ["Oceania ↔ Asia", "Oceania ↔ North America"],
    countries: ["Australia", "New Zealand", "Papua New Guinea", "Fiji"],
  },
];

export const FREIGHT_ROUTES = {
  "usa-to-zambia": {
    slug: "usa-to-zambia",
    hero: {
      eyebrow: "USA → Zambia",
      title: "Fast, reliable freight shipping from the USA to Zambia",
      subtitle:
        "Door-to-door ocean and air freight from any US city to Lusaka, Ndola, Kitwe, and beyond. Consolidation at origin, documentation, and customs clearance handled by our team.",
      metaTitle: "USA to Zambia Freight Shipping — Turbo Transit",
      metaDescription:
        "Ship freight from the USA to Zambia — door-to-door FCL, LCL, and air cargo with customs clearance and inland delivery to Lusaka, Ndola, Kitwe, and Livingstone.",
    },
    defaults: {
      mode: "ocean_lcl",
      origin: "USA",
      destination: "Zambia",
      originPort: "New York (USNYC)",
      destinationPort: "Dar es Salaam (TZDAR) → Lusaka",
    },
    lanes: [
      { size: "20ft FCL", eta: "35–50 days", note: "Port to Lusaka warehouse" },
      { size: "40ft FCL", eta: "35–50 days", note: "Port to Lusaka / Ndola" },
      { size: "LCL", eta: "45–60 days", note: "Consolidated weekly departures" },
      { size: "Air cargo", eta: "7–14 days", note: "For urgent freight" },
    ],
    services: [
      "Origin consolidation at our Troy, MI warehouse",
      "Ocean & air freight with top carriers",
      "Full documentation (BOL, packing list, invoice, C.O.O.)",
      "Zambia customs clearance & duty advisory",
      "Inland trucking to Lusaka, Ndola, Kitwe, Livingstone",
      "Real-time tracking with SMS + email updates",
    ],
    faqs: [
      { q: "How long does shipping from the USA to Zambia take?", a: "Ocean FCL from a US East Coast port typically takes 35–50 days port-to-door via Dar es Salaam with inland trucking. LCL runs 45–60 days. Air freight is 7–14 days for time-sensitive cargo." },
      { q: "Can you handle customs clearance in Zambia?", a: "Yes — our partner brokers in Lusaka clear cargo through ZRA and coordinate release, storage, and inland delivery. We advise on HS codes and duty exposure before you ship." },
      { q: "Do you accept personal effects and household goods?", a: "Yes — barrels, boxes, and full households can be consolidated at our Troy hub. Diaspora shipments are one of our largest lanes." },
      { q: "What documents do I need?", a: "Commercial invoice, packing list, and (for household goods) an unaccompanied baggage declaration. Vehicle shipments need title + bill of sale. We prepare the BOL and export docs on your behalf." },
    ],
  },

  "china-to-africa": {
    slug: "china-to-africa",
    hero: {
      eyebrow: "China → Africa",
      title: "Bulk freight from China to Africa — door to door",
      subtitle:
        "Move containers, pallets, and consolidated LCL cargo from Shanghai, Ningbo, Shenzhen, and Guangzhou to major African ports. Guaranteed space, competitive rates, and we handle documentation, consolidation, and clearance.",
      metaTitle: "China to Africa Bulk Freight Shipping — Turbo Transit",
      metaDescription:
        "Ship bulk freight from China to Africa — FCL and LCL ocean freight from Shanghai, Ningbo, Shenzhen, and Guangzhou with consolidation, documentation, and customs clearance at destination.",
    },
    defaults: {
      mode: "ocean_fcl",
      origin: "China",
      destination: "Africa",
      originPort: "Shanghai (CNSHA)",
      destinationPort: "Dar es Salaam (TZDAR)",
    },
    lanes: [
      { size: "20ft FCL", eta: "28–40 days", note: "China → East Africa main ports" },
      { size: "40ft FCL", eta: "28–40 days", note: "China → West / South Africa" },
      { size: "40ft HC", eta: "28–40 days", note: "High volume, light cargo" },
      { size: "LCL", eta: "40–55 days", note: "Weekly consolidation from Shanghai" },
    ],
    services: [
      "Guaranteed space with COSCO, MSC, Maersk, ONE",
      "Origin consolidation at Shanghai, Ningbo, Shenzhen, Guangzhou",
      "Full export documentation & customs at origin",
      "Destination clearance in Tanzania, Kenya, Nigeria, South Africa, Ghana, Zambia",
      "Inland trucking & rail to landlocked destinations",
      "Insurance and DG cargo handling on request",
    ],
    faqs: [
      { q: "Which African ports do you serve?", a: "We regularly move cargo into Dar es Salaam, Mombasa, Durban, Cape Town, Lagos Apapa/Tin Can, Tema, Lomé, and Djibouti — with inland forwarding beyond." },
      { q: "What's the typical transit time China to East Africa?", a: "20–28 days port-to-port and 28–40 days door-to-door via Shanghai/Ningbo → Dar es Salaam or Mombasa, plus inland trucking." },
      { q: "Can you consolidate multiple suppliers in China?", a: "Yes — we pick up from multiple suppliers, consolidate at our Shanghai hub, and ship as one FCL or LCL. Great for wholesale buyers." },
      { q: "How do you price LCL?", a: "LCL is priced per CBM with a minimum of 1 CBM. Rates depend on lane and season — we quote firm within one business day." },
    ],
  },

  "china-to-europe": {
    slug: "china-to-europe",
    hero: {
      eyebrow: "China ↔ Europe",
      title: "Ocean & rail freight between China and Europe",
      subtitle:
        "Two ways to move cargo across the New Silk Road. FCL and LCL ocean freight with guaranteed space, or 16–22 day trans-Eurasian rail on scheduled block trains — up to 50% faster than sea, 70% cheaper than air.",
      metaTitle: "China to Europe Freight Shipping (Ocean & Rail) — Turbo Transit",
      metaDescription:
        "Ship freight between China and Europe — FCL, LCL ocean freight or trans-Eurasian rail with 16–22 day transit. Door-to-door with documentation and customs handled.",
    },
    defaults: {
      mode: "rail",
      origin: "China",
      destination: "Europe",
      originPort: "Xi'an / Chengdu",
      destinationPort: "Duisburg / Hamburg",
    },
    lanes: [
      { size: "Rail FCL", eta: "16–22 days", note: "Xi'an / Chengdu → Duisburg / Łódź" },
      { size: "Rail LCL", eta: "18–24 days", note: "Weekly block-train departures" },
      { size: "Ocean 40ft FCL", eta: "30–45 days", note: "Shanghai → Rotterdam / Hamburg" },
      { size: "Ocean LCL", eta: "40–55 days", note: "Weekly consolidation" },
    ],
    services: [
      "Trans-Eurasian block-train service (New Silk Road)",
      "Ocean FCL & LCL with COSCO, MSC, Maersk",
      "Border transshipment & broad-gauge handling",
      "EU customs clearance and IOSS/VAT advisory",
      "Door-to-door to Germany, Netherlands, Poland, UK, France, Italy",
      "Lower CO₂ than air, faster than ocean",
    ],
    faqs: [
      { q: "Why choose rail over ocean between China and Europe?", a: "Rail is roughly half the transit time of ocean (16–22 days vs 30–45) at about 30–40% of the cost of air freight. It's ideal for time-sensitive but non-urgent cargo, and has a much lower carbon footprint than air." },
      { q: "Which rail routes do you use?", a: "Primarily via Alashankou / Khorgos and Erenhot border crossings on scheduled block trains to Duisburg, Hamburg, Łódź, Malaszewicze, and Madrid — with onward trucking across the EU." },
      { q: "Are rail rates stable?", a: "Rail rates are more stable than ocean spot rates but subject to seasonal capacity. We lock in weekly departures and give you a firm rate before you commit." },
      { q: "Can you handle EU customs?", a: "Yes — our EU partners clear cargo, handle T1 transit, and advise on IOSS/VAT for e-commerce and DDP shipments." },
    ],
  },
};
